/**
 * Wire-message Zod schemas for the StudyGroupDo leaderboard's per-card
 * push and the single-sender ack.
 *
 * ### Centi-MP convention (READ FIRST)
 * Every MP value on this wire — the client-submitted `delta` and the
 * server-returned `totalMP` / `todayMP` — is an **integer in centi-MP**
 * (1 MP = 100 units). The client computes
 * `Math.round(eventPoints * multiplier * 100)` per card and pushes the
 * integer. `SGScore` storage carries integers; the REST `/leaderboard`
 * endpoint and the UI consumers divide by 100 + round at the display
 * boundary.
 *
 * Why: the "round on aggregate, never per-event" invariant requires
 * the server to never round mid-aggregation. Centi-MP keeps storage in
 * integers and satisfies that invariant without ever holding floats.
 *
 * ### Authority / dispatch
 * The `sgScores` entity is REGISTERED in `entities.ts`, but the wire
 * here is INTERCEPTED by a custom handler in
 * `flashka-web/party/server/studygroupdo.ts` (`customHandlers
 * ['sg-score-delta']`). Registration only gives the client store a
 * known slice + LWW merger; per-card flow does NOT travel through the
 * framework's record-push pipeline. The server responds to the sender
 * only — no broadcast.
 */

import { z } from 'zod';

/**
 * The ten scoring modalities. Matches `EVENT_POINTS` /
 * `scoreForOralExamSession` / `scoreForCaseStudySession` in
 * `shared/src/sync-client/study-groups/scoring.ts`:
 *
 *   - `sr_correct` / `quiz_correct` / `exam_correct` → 3 base MP per card
 *   - `sr_wrong` / `fr_review` / `quiz_wrong` / `exam_wrong`
 *                                                   → 1 base MP per card
 *   - `sr_review`                                   → 3 base MP per card,
 *     LEGACY. The grade-blind spaced-repetition event, replaced by the
 *     `sr_correct` / `sr_wrong` pair. New clients never send it; old app
 *     builds send nothing else, so this member is ADDITIVE-ONLY and must
 *     never be removed while those builds are in the wild. Deploy order
 *     follows from the same fact in the other direction: the worker has
 *     to learn the two new members BEFORE any client sends them, or the
 *     schema rejects the frame and the MP is simply lost.
 *   - `oral_exam_session`                           → up to 50 base MP
 *     per finished oral exam, proportional to the score percent (the
 *     PER-SESSION rather than per-card type — the wire still
 *     carries a single `delta` so the server stays uniform; only the
 *     client-side formula differs).
 *   - `case_study_session`                          → up to 50 base MP
 *     per finished case study, same formula and shape as
 *     `oral_exam_session` (both are per-SESSION variants that fan out
 *     one push per linked group at completion time).
 *
 * Underscore convention matches the rest of the data layer
 * (`study_group`, `deck_data_compact`, Postgres-style). Quiz vs exam
 * vs oral_exam_session vs case_study_session stay distinct so the
 * server can authoritatively re-verify `delta` later (anti-cheat) and
 * so per-mode analytics / features don't need a wire migration.
 */
export const sgScoreEventTypeSchema = z.enum([
  'sr_review',
  'sr_correct',
  'sr_wrong',
  'fr_review',
  'quiz_correct',
  'quiz_wrong',
  'exam_correct',
  'exam_wrong',
  'oral_exam_session',
  'case_study_session',
]);
export type SGScoreEventType = z.infer<typeof sgScoreEventTypeSchema>;

/**
 * Per-message ceiling for a study-time credit, in ms (2 hours). Shared
 * between the wire schemas below, the client-side drain clamp
 * (`takeStudyTimeDeltaMsForWire`), and the server's value-domain checks
 * in `score-delta-handler.ts`. A legit client flushes every ~60s (or per
 * card), so real deltas sit orders of magnitude below this — the ceiling
 * exists to bound what a hand-rolled client can farm per frame.
 * (Declared ahead of the schemas — both reference it at module init.)
 */
export const MAX_TIME_DELTA_MS = 7_200_000;

/**
 * Client → server per-card push. `preprocessMessage` in
 * `studygroupdo.ts` stamps the authenticated `userId` onto the
 * message before dispatch (same idiom as `sg-activity-push` at
 * studygroupdo.ts:357-368), so this wire deliberately does NOT carry
 * `userId` — sender authority only.
 */
export const sgScoreDeltaMessageSchema = z
  .object({
    type: z.literal('sg-score-delta'),
    /**
     * Centi-MP integer for this single card. Client computes
     * `Math.round(eventPoints * multiplier * 100)`. Server rejects
     * `delta <= 0` in `applyScoreDelta`, so the schema gates positive-
     * only here to keep the wire contract aligned with the handler.
     */
    delta: z.number().int().positive(),
    cardId: z.string(),
    eventType: sgScoreEventTypeSchema,
    /**
     * The user-local calendar day this delta was earned in. Drives the
     * lazy `todayMP` reset and the streak bump (max once per session).
     * Format `YYYY-MM-DD`.
     */
    userLocalDayISO: z.string(),
    /**
     * Full ISO 8601 UTC instant of the delta event. Drives the lazy
     * `weekMP` reset (Mon 00:00 UTC boundary) and the `monthlyMP`
     * bucket key (`YYYY-MM` slice).
     */
    utcAtISO: z.string(),
    /**
     * Optional sender-side correlation id, consistent with the other
     * custom-message conventions. Unused by the server in v1.
     */
    mutationId: z.string().optional(),
    /**
     * OPTIONAL piggybacked study-time credit, integer MILLISECONDS.
     * Carries the ms the study-time tracker accumulated since its
     * previous drain (see `sync-client/study-groups/study-time.ts`), so
     * per-card flows ship time at card-commit granularity without an
     * extra frame. Old clients simply omit the field (`.optional()` is
     * the backward-compat contract); old SERVERS ignore it via
     * `.passthrough()` — safe skew in both directions.
     *
     * Credits the row's time buckets only — never MP, never the streak.
     */
    timeDeltaMs: z.number().int().nonnegative().max(MAX_TIME_DELTA_MS).optional(),
  })
  .passthrough();

export type SGScoreDeltaMessage = z.infer<typeof sgScoreDeltaMessageSchema>;

/**
 * Client → server dedicated study-time push. The sibling of the
 * `timeDeltaMs` piggyback above for moments with no score frame to ride
 * on: the periodic in-session flush (~60s), the on-hide flush, and the
 * end-of-session flush — which is what makes time land live for oral
 * exam / case study (their MP frame only fires after grading) and what
 * catches the tail between the last card commit and an early exit.
 *
 * Same sender-authority model as `sg-score-delta`: no `userId` on the
 * wire, `preprocessMessage` stamps the authenticated sender. Handled by
 * a custom handler in `studygroupdo.ts` → `applyTimeDelta`; NO ack, no
 * broadcast (leaderboards hydrate via REST). Never touches MP or the
 * streak.
 */
export const sgTimeDeltaMessageSchema = z
  .object({
    type: z.literal('sg-time-delta'),
    /** Tracked study time since the previous drain, integer ms > 0. */
    timeDeltaMs: z.number().int().positive().max(MAX_TIME_DELTA_MS),
    /**
     * The user-local calendar day the time was tracked in. Drives the
     * lazy `todayTimeMs` reset (shared anchor with `todayMP`).
     */
    userLocalDayISO: z.string(),
    /**
     * Full ISO 8601 UTC instant of the flush. Drives the lazy
     * `weekTimeMs` reset (shared anchor with `weekMP`) and the
     * `monthlyTimeMs` bucket key (`YYYY-MM` slice).
     */
    utcAtISO: z.string(),
    /** Optional sender-side correlation id — convention parity. */
    mutationId: z.string().optional(),
  })
  .passthrough();

export type SGTimeDeltaMessage = z.infer<typeof sgTimeDeltaMessageSchema>;

/**
 * Server → sender single-sender response. NO broadcast — other members
 * pull score changes via REST `/api/study-groups/:id/leaderboard` on
 * next dashboard open.
 */
export const sgScoreDeltaAckSchema = z
  .object({
    type: z.literal('sg-score-ack'),
    /** Post-bump streak in days. The client uses this for the NEXT card's multiplier. */
    streak: z.number().int().nonnegative(),
    /** Updated lifetime total, CENTI-MP integer. Divide by 100 for display. */
    totalMP: z.number().int().nonnegative(),
    /** Updated user-local today total, CENTI-MP integer. Divide by 100 for display. */
    todayMP: z.number().int().nonnegative(),
  })
  .passthrough();

export type SGScoreDeltaAck = z.infer<typeof sgScoreDeltaAckSchema>;
